// Currency helper functions for multilingual support

const SUPPORTED_CURRENCIES = {
    AED: {
        code: 'AED',
        symbol: 'د.إ',
        en: { name: 'UAE Dirham', shortName: 'AED' },
        ar: { name: 'درهم إماراتي', shortName: 'د.إ' }
    },
    USD: {
        code: 'USD',
        symbol: '$',
        en: { name: 'US Dollar', shortName: 'USD' },
        ar: { name: 'دولار أمريكي', shortName: '$' }
    },
    EUR: {
        code: 'EUR',
        symbol: '€',
        en: { name: 'Euro', shortName: 'EUR' },
        ar: { name: 'يورو', shortName: '€' }
    },
    GBP: {
        code: 'GBP',
        symbol: '£',
        en: { name: 'British Pound', shortName: 'GBP' },
        ar: { name: 'جنيه إسترليني', shortName: '£' }
    },
    SAR: {
        code: 'SAR',
        symbol: '﷼',
        en: { name: 'Saudi Riyal', shortName: 'SAR' },
        ar: { name: 'ريال سعودي', shortName: '﷼' }
    },
    QAR: {
        code: 'QAR',
        symbol: 'ق.ر',
        en: { name: 'Qatari Riyal', shortName: 'QAR' },
        ar: { name: 'ريال قطري', shortName: 'ق.ر' }
    },
    KWD: {
        code: 'KWD',
        symbol: 'د.ك',
        en: { name: 'Kuwaiti Dinar', shortName: 'KWD' },
        ar: { name: 'دينار كويتي', shortName: 'د.ك' }
    },
    BHD: {
        code: 'BHD',
        symbol: 'د.ب',
        en: { name: 'Bahraini Dinar', shortName: 'BHD' },
        ar: { name: 'دينار بحريني', shortName: 'د.ب' }
    },
    OMR: {
        code: 'OMR',
        symbol: 'ر.ع',
        en: { name: 'Omani Rial', shortName: 'OMR' },
        ar: { name: 'ريال عماني', shortName: 'ر.ع' }
    },
    INR: {
        code: 'INR',
        symbol: '₹',
        en: { name: 'Indian Rupee', shortName: 'INR' },
        ar: { name: 'روبية هندية', shortName: '₹' }
    },
    PKR: {
        code: 'PKR',
        symbol: '₨',
        en: { name: 'Pakistani Rupee', shortName: 'PKR' },
        ar: { name: 'روبية باكستانية', shortName: '₨' }
    }
};

const DEFAULT_COUNTRIES = {
    UAE: {
        code: 'UAE',
        en: { name: 'United Arab Emirates' },
        ar: { name: 'دولة الإمارات العربية المتحدة' }
    },
    KSA: {
        code: 'KSA',
        en: { name: 'Saudi Arabia' },
        ar: { name: 'المملكة العربية السعودية' }
    },
    QAT: {
        code: 'QAT',
        en: { name: 'Qatar' },
        ar: { name: 'قطر' }
    },
    KWT: {
        code: 'KWT',
        en: { name: 'Kuwait' },
        ar: { name: 'الكويت' }
    },
    BHR: {
        code: 'BHR',
        en: { name: 'Bahrain' },
        ar: { name: 'البحرين' }
    },
    OMN: {
        code: 'OMN',
        en: { name: 'Oman' },
        ar: { name: 'عُمان' }
    },
    EGY: {
        code: 'EGY',
        en: { name: 'Egypt' },
        ar: { name: 'مصر' }
    },
    JOR: {
        code: 'JOR',
        en: { name: 'Jordan' },
        ar: { name: 'الأردن' }
    },
    LBN: {
        code: 'LBN',
        en: { name: 'Lebanon' },
        ar: { name: 'لبنان' }
    },
    IND: {
        code: 'IND',
        en: { name: 'India' },
        ar: { name: 'الهند' }
    },
    PAK: {
        code: 'PAK',
        en: { name: 'Pakistan' },
        ar: { name: 'باكستان' }
    }
};

/**
 * Get currency display name based on language
 * @param {string} currencyCode - Currency code (e.g., 'AED', 'USD')
 * @param {string} language - Language code ('en' or 'ar')
 * @param {boolean} shortForm - Return short form or full name
 * @returns {string} Currency display name
 */
export const getCurrencyName = (currencyCode, language = 'en', shortForm = false) => {
    const currency = SUPPORTED_CURRENCIES[currencyCode];
    if (!currency) return currencyCode;

    const langData = currency[language] || currency.en;
    return shortForm ? langData.shortName : langData.name;
};

/**
 * Get country display name based on language
 * @param {string} countryCode - Country code (e.g., 'UAE', 'KSA')
 * @param {string} language - Language code ('en' or 'ar')
 * @returns {string} Country display name
 */
export const getCountryName = (countryCode, language = 'en') => {
    const country = DEFAULT_COUNTRIES[countryCode];
    if (!country) return countryCode;

    const langData = country[language] || country.en;
    return langData.name;
};

/**
 * Format currency amount with proper symbol and direction
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {string} language - Language code ('en' or 'ar')
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode, language = 'en', options = {}) => {
    // console.log('formatCurrency called with:', { amount, currencyCode, language, options });

    const {
        showSymbol = true,
        showCode = false,
        useShortName = false,
        decimals = 2,
        spaceBetween = true
    } = options;

    const currency = SUPPORTED_CURRENCIES[currencyCode];
    if (!currency) {
        const formattedAmount = Number(amount || 0).toFixed(decimals);
        return `${formattedAmount} ${currencyCode}`;
    }

    const formattedAmount = Number(amount || 0).toFixed(decimals);
    // Use language-specific symbol/shortName based on the selected language
    const languageSpecificSymbol = currency[language]?.shortName || currency.en.shortName;
    const shortName = currency[language]?.shortName || currency.en.shortName;
    const space = spaceBetween ? ' ' : '';

    let currencyDisplay = '';

    if (showSymbol && showCode) {
        currencyDisplay = useShortName ? shortName : `${languageSpecificSymbol} (${currencyCode})`;
    } else if (showCode) {
        currencyDisplay = currencyCode;
    } else if (useShortName) {
        currencyDisplay = shortName;
    } else {
        // Use the language-specific symbol instead of the generic symbol
        currencyDisplay = languageSpecificSymbol;
    }

    // For Arabic (RTL), put currency after the number
    if (language === 'ar') {
        // console.log('Using Arabic format:', `${formattedAmount}${space}${currencyDisplay}`);
        return `${formattedAmount}${space}${currencyDisplay}`;
    } else {
        // For English (LTR), put currency before the number
        // console.log('Using English format:', `${currencyDisplay}${space}${formattedAmount}`);
        return `${currencyDisplay}${space}${formattedAmount}`;
    }
};

/**
 * Format currency amount for display - simplified version
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {string} language - Language code
 * @returns {string} Formatted currency string
 */
export const formatCurrencyDisplay = (amount, currencyCode, language = 'en') => {
    return formatCurrency(amount, currencyCode, language, {
        showSymbol: true,
        showCode: false,
        useShortName: false,
        decimals: 2,
        spaceBetween: true
    });
};

/**
 * Format currency amount with short name (for compact displays)
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {string} language - Language code
 * @returns {string} Formatted currency string with short name
 */
export const formatCurrencyCompact = (amount, currencyCode, language = 'en') => {
    return formatCurrency(amount, currencyCode, language, {
        showSymbol: false,
        showCode: false,
        useShortName: true,
        decimals: 2,
        spaceBetween: true
    });
};

/**
 * Format currency amount without decimals (for whole numbers)
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {string} language - Language code
 * @returns {string} Formatted currency string without decimals
 */
export const formatCurrencyWhole = (amount, currencyCode, language = 'en') => {
    return formatCurrency(amount, currencyCode, language, {
        showSymbol: true,
        showCode: false,
        useShortName: false,
        decimals: 0,
        spaceBetween: true
    });
};

/**
 * Get currency symbol for a specific language
 * @param {string} currencyCode - Currency code
 * @param {string} language - Language code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode, language = 'en') => {
    const currency = SUPPORTED_CURRENCIES[currencyCode];
    if (!currency) return currencyCode;

    // Return language-specific short name instead of generic symbol
    return currency[language]?.shortName || currency.en.shortName;
};

/**
 * Get currency short name for a specific language
 * @param {string} currencyCode - Currency code
 * @param {string} language - Language code
 * @returns {string} Currency short name
 */
export const getCurrencyShortName = (currencyCode, language = 'en') => {
    const currency = SUPPORTED_CURRENCIES[currencyCode];
    if (!currency) return currencyCode;

    return currency[language]?.shortName || currency.en.shortName;
};

/**
 * Create a currency formatter function with preset options
 * @param {string} currencyCode - Currency code
 * @param {string} language - Language code
 * @param {object} options - Formatting options
 * @returns {function} Currency formatter function
 */
export const createCurrencyFormatter = (currencyCode, language = 'en', options = {}) => {
    return (amount) => formatCurrency(amount, currencyCode, language, options);
};

/**
 * Parse currency amount from string (removes currency symbols and formatting)
 * @param {string} currencyString - Formatted currency string
 * @returns {number} Parsed amount
 */
export const parseCurrencyAmount = (currencyString) => {
    if (typeof currencyString === 'number') {
        return currencyString;
    }

    if (typeof currencyString !== 'string') {
        return 0;
    }

    // Remove all non-numeric characters except decimal point and minus sign
    const cleanString = currencyString.replace(/[^\d.-]/g, '');
    const amount = parseFloat(cleanString);

    return isNaN(amount) ? 0 : amount;
};



/**
 * React hook for currency formatting with language context
 * This hook should be used within React components
 * @param {string} currencyCode - Currency code from store settings
 * @param {string} language - Current language from language context
 * @returns {object} Currency formatting functions
 */
export const useCurrencyFormatter = (currencyCode, language = 'en') => {
    // Note: This requires React's useCallback to be imported in the consuming component
    const formatAmount = (amount, options) => {
        return formatCurrency(amount, currencyCode, language, options);
    };

    const formatDisplay = (amount) => {
        return formatCurrencyDisplay(amount, currencyCode, language);
    };

    const formatCompact = (amount) => {
        return formatCurrencyCompact(amount, currencyCode, language);
    };

    const formatWhole = (amount) => {
        return formatCurrencyWhole(amount, currencyCode, language);
    };

    const getSymbol = () => {
        return getCurrencySymbol(currencyCode, language);
    };

    const getShortName = () => {
        return getCurrencyShortName(currencyCode, language);
    };

    return {
        formatAmount,
        formatDisplay,
        formatCompact,
        formatWhole,
        getSymbol,
        getShortName,
        parseCurrencyAmount
    };
};

// Export all utility functions for direct use
export {
    formatCurrency as default,
    SUPPORTED_CURRENCIES,
    DEFAULT_COUNTRIES
};
